import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * BrandButton Component - Light Theme Editorial Design
 *
 * Clean, minimal button variants for the DeTrust platform:
 * - primary: Teal solid (primary CTA)
 * - secondary: Light background with border (secondary actions)
 * - outline: Teal outlined
 * - ghost: Subtle with hover effects
 * - destructive: For dangerous actions
 * - link: Text-only button
 *
 * All variants maintain WCAG AA accessibility standards
 */

const brandButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold tracking-wide ring-offset-background transition-all duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        // Primary CTA - the theme's primary fill with the ink the theme
        // pairs with it. Never `text-white`: inside the app shell `.dt-vnext`
        // remaps `--primary` (D2), and in dark that fill is #83a9ff — white
        // reads 2.31:1 on it, `--primary-foreground` (#081933) 7.59:1.
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md active:scale-[0.98]",

        // Secondary - Light background with border
        secondary:
          "bg-secondary text-foreground border border-border hover:bg-secondary/80 shadow-sm",

        // Outline - Teal border
        outline:
          "border border-primary/50 text-primary bg-transparent hover:bg-primary/5 transition-all",

        // Ghost - Subtle, minimal
        ghost:
          "text-muted-foreground hover:text-foreground hover:bg-secondary transition-all",

        // Destructive - the same rule: remapped in dark to #ffacba, which
        // carries `--destructive-foreground`, not white.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md",

        // Link - Text-only button
        link: "text-primary hover:text-primary/80 underline-offset-4 hover:underline transition-colors",

        // Market-specific variants (kept for compatibility)
        auction:
          "bg-amber-600 hover:bg-amber-700 text-white shadow-sm hover:shadow-md active:scale-[0.98]",

        uniswap:
          "bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md active:scale-[0.98]",

        pendle:
          "bg-gradient-pendle text-white shadow-sm hover:shadow-md active:scale-[0.98]",

        // Success variant - for positive actions
        success:
          "bg-primary hover:bg-brand-blue-700 text-primary-foreground shadow-sm hover:shadow-md font-semibold",
      },
      size: {
        sm: "h-9 px-4 py-2 text-sm rounded-md",
        default: "h-10 px-6 py-3 text-sm rounded-lg",
        lg: "h-11 px-8 py-3 text-base rounded-lg",
        xl: "h-14 px-10 py-4 text-lg rounded-xl",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface BrandButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof brandButtonVariants> {
  asChild?: boolean;
  /**
   * Loading state
   */
  loading?: boolean;
  /**
   * Full width button
   */
  fullWidth?: boolean;
}

const BrandButton = React.forwardRef<HTMLButtonElement, BrandButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          brandButtonVariants({ variant, size }),
          fullWidth && "w-full",
          className,
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg
              className="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Processing...</span>
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

BrandButton.displayName = "BrandButton";

export { BrandButton };

/**
 * Specialized Button Components
 */

/**
 * Connect Wallet Button - Primary CTA
 */
export const ConnectWalletButton = React.forwardRef<
  HTMLButtonElement,
  Omit<BrandButtonProps, "variant">
>(({ children = "Connect Wallet", ...props }, ref) => (
  <BrandButton ref={ref} variant="primary" size="lg" {...props}>
    {children}
  </BrandButton>
));

ConnectWalletButton.displayName = "ConnectWalletButton";

/**
 * Market Action Button - for specific market types
 */
export interface MarketActionButtonProps extends Omit<
  BrandButtonProps,
  "variant"
> {
  marketType: "auction" | "uniswap" | "pendle";
}

export const MarketActionButton = React.forwardRef<
  HTMLButtonElement,
  MarketActionButtonProps
>(({ marketType, ...props }, ref) => (
  <BrandButton ref={ref} variant={marketType} {...props} />
));

MarketActionButton.displayName = "MarketActionButton";

/**
 * Submit Transaction Button - Primary action with loading state
 */
export interface SubmitTransactionButtonProps extends Omit<
  BrandButtonProps,
  "variant"
> {
  transacting?: boolean;
}

export const SubmitTransactionButton = React.forwardRef<
  HTMLButtonElement,
  SubmitTransactionButtonProps
>(({ children = "Submit Transaction", transacting = false, ...props }, ref) => (
  <BrandButton
    ref={ref}
    variant="primary"
    size="lg"
    loading={transacting}
    {...props}
  >
    {children}
  </BrandButton>
));

SubmitTransactionButton.displayName = "SubmitTransactionButton";

/**
 * Button Group - for grouped actions
 */
export interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  orientation?: "horizontal" | "vertical";
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = "horizontal",
}) => {
  return (
    <div
      className={cn(
        "inline-flex",
        orientation === "horizontal" ? "flex-row gap-3" : "flex-col gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

ButtonGroup.displayName = "ButtonGroup";
